import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import type { CardSummary } from '../lib/api';
import { Link } from '../lib/router';
import { prefs, toasts } from '../lib/store';

/** Type label for covers without a photograph: a mono data plate on black, never an emoji. */
export const TYPE_LABEL: Record<string, string> = { recipe: 'Recipe', note: 'Note' };
export const typeLabel = (t: string) => TYPE_LABEL[t] ?? t;

export function Stars({ value }: { value: number | null }) {
  if (!value) return null;
  return (
    <span class="stars" aria-label={`${value} out of 5 stars`}>
      {'★'.repeat(value)}
      <span class="muted">{'★'.repeat(5 - value)}</span>
    </span>
  );
}

export function StarInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <div class="row" role="group" aria-label="Rating" style={{ flexWrap: 'nowrap', gap: 0 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button type="button" class="star-btn" aria-pressed={!!value && n <= value} aria-label={`${n} star${n > 1 ? 's' : ''}`} onClick={() => onChange(value === n ? null : n)}>
          ★
        </button>
      ))}
    </div>
  );
}

/** Renders a search snippet whose matches are delimited by \u0002 … \u0003 — as text, never HTML. */
export function Snippet({ text }: { text: string }) {
  const parts = text.split(/(\u0002[^\u0003]*\u0003)/);
  return (
    <span>
      {parts.map((p) => (p.startsWith('\u0002') ? <mark>{p.slice(1, -1)}</mark> : p))}
    </span>
  );
}

export function minutes(m: number | null | undefined): string {
  if (!m) return '';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h${m % 60 ? ` ${m % 60} min` : ''}`;
}

export function CardTile({ c }: { c: CardSummary }) {
  const diet = c.tags.find((t) => ['vegetarian', 'vegan', 'gluten-free'].includes(t));
  const face = [minutes(c.face.total_min), c.face.servings, diet === 'vegetarian' ? 'veg' : diet].filter(Boolean).join(' · ');
  return (
    <Link class="card dr-frame dr-frame--tight" href={`/c/${c.id}/${c.slug}`}>
      <div class="card-cover dr-photo" aria-hidden="true">
        {c.cover ? <img src={c.cover.thumb_url} alt="" loading="lazy" /> : <span class="dr-plate">{typeLabel(c.type)}</span>}
      </div>
      <div class="card-body">
        <div class="card-title">
          <span>{c.title}</span>
          {c.favourite && <span aria-label="favourite" class="stars">★</span>}
        </div>
        {c.snippet ? (
          <div class="card-summary">
            <Snippet text={c.snippet} />
          </div>
        ) : (
          c.summary && <div class="card-summary">{c.summary}</div>
        )}
        {face && <div class="card-face dr-plate">{face}</div>}
        <div class="row">
          {c.needs_review && <span class="dr-tag dr-tag--alert">Needs review</span>}
          {c.status === 'draft' && <span class="dr-tag bk-draft">Draft</span>}
          {c.status === 'archived' && <span class="dr-tag bk-archived">Archived</span>}
        </div>
      </div>
    </Link>
  );
}

export function CardGrid({ items, density }: { items: CardSummary[]; density?: string }) {
  const d = density ?? prefs.use().density;
  return (
    <div class={`grid ${d === 'comfortable' ? '' : d}`}>
      {items.map((c) => (
        <CardTile key={c.id} c={c} />
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: [T, string][]; onChange: (v: T) => void; label: string }) {
  return (
    <div class="dr-segmented" role="group" aria-label={label}>
      {options.map(([v, l]) => (
        <button type="button" aria-pressed={v === value} onClick={() => onChange(v)}>
          {l}
        </button>
      ))}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ComponentChildren; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEscape(onClose);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('input, textarea, select, button')?.focus();
    return () => prev?.focus();
  }, []);
  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div class="row-between">
          <h2>{title}</h2>
          <button type="button" class="dr-btn bk-ghost dr-btn--icon" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ComponentChildren }) {
  useEscape(onClose);
  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <aside class="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div class="row-between">
          <h2>{title}</h2>
          <button type="button" class="dr-btn bk-ghost dr-btn--icon" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </aside>
    </div>
  );
}

export function useEscape(fn: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && fn();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [fn]);
}

export function Toasts() {
  const list = toasts.use();
  return (
    <div class="toasts" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} class={`toast ${t.kind === 'error' ? 'toast-error' : ''}`}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function Loading() {
  return <p class="muted">Loading…</p>;
}

export function ErrorBox({ error }: { error: unknown }) {
  return <p class="error" role="alert">{error instanceof Error ? error.message : String(error)}</p>;
}

export function Empty({ children }: { children: ComponentChildren }) {
  return <div class="empty">{children}</div>;
}
